import React, { useState } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth, USER_TIERS } from '../contexts/AuthContext';
import { useCommunity, POST_TYPES, CATEGORIES } from '../contexts/CommunityContext';
import { useNotification, NOTIFICATION_TYPES } from '../contexts/NotificationContext';
import Sidebar from '../components/layout/Sidebar';
import './TestPage.css';

// 공지사항 섹션
const AnnouncementsSection = ({ onPostSelect }) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { getAnnouncements, createPost, deletePost } = useCommunity();

  const [showWriteForm, setShowWriteForm] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [newContent, setNewContent] = useState('');
  const [message, setMessage] = useState('');

  const isAdmin = user?.tier === USER_TIERS.ADMIN;
  const announcements = getAnnouncements();

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('ko-KR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const handleCreatePost = (e) => {
    e.preventDefault();
    if (!newTitle.trim() || !newContent.trim()) return;

    createPost(POST_TYPES.ANNOUNCEMENT, newTitle, newContent, user, null, []);
    setNewTitle('');
    setNewContent('');
    setShowWriteForm(false);
    setMessage('공지사항이 등록되었습니다.');
    setTimeout(() => setMessage(''), 3000);
  };

  return (
    <div className="section-container">
      <div className="section-header">
        <h1>📢 {t('community.tabs.announcements', '공지사항')}</h1>
        <p>중요한 공지사항을 확인하세요</p>
      </div>

      {message && <div className="message success">{message}</div>}

      {isAdmin && !showWriteForm && (
        <button className="write-btn" onClick={() => setShowWriteForm(true)}>
          새 공지 작성
        </button>
      )}

      {showWriteForm && (
        <form onSubmit={handleCreatePost} className="write-form">
          <input
            type="text"
            value={newTitle}
            onChange={(e) => setNewTitle(e.target.value)}
            placeholder="공지사항 제목"
            required
          />
          <textarea
            value={newContent}
            onChange={(e) => setNewContent(e.target.value)}
            placeholder="공지사항 내용"
            rows={6}
            required
          />
          <div className="form-actions">
            <button type="button" onClick={() => setShowWriteForm(false)} className="cancel-btn">
              취소
            </button>
            <button type="submit" className="submit-btn">
              등록
            </button>
          </div>
        </form>
      )}

      <div className="posts-list">
        {announcements.length === 0 ? (
          <p className="no-posts">등록된 공지사항이 없습니다.</p>
        ) : (
          announcements.map((post) => (
            <div key={post.id} className="post-card" onClick={() => onPostSelect(post)}>
              <div className="post-card-header">
                <span className="post-badge announcement">공지</span>
                <h3>{post.title}</h3>
              </div>
              <div className="post-card-meta">
                <span className="author">{post.author.name}</span>
                <span className="date">{formatDate(post.createdAt)}</span>
                <span className="comments">댓글 {post.comments?.length || 0}</span>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

// 자유게시판 섹션
const FreeBoardSection = ({ onPostSelect, selectedCategory, onCategorySelect }) => {
  const { t } = useTranslation();
  const { user, isAuthenticated } = useAuth();
  const { getCommunityPosts, getCategoryCount, createPost } = useCommunity();

  const [showWriteForm, setShowWriteForm] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [newContent, setNewContent] = useState('');
  const [newCategory, setNewCategory] = useState(CATEGORIES.GENERAL);
  const [message, setMessage] = useState('');

  const canPost = isAuthenticated && user?.tier !== USER_TIERS.GUEST;
  const posts = getCommunityPosts(selectedCategory);
  const categoryCounts = getCategoryCount();

  // 카테고리 한글 이름
  const categoryLabels = {
    all: '전체',
    general: '일반',
    question: '질문',
    discussion: '토론',
    showcase: '자랑',
    tip: '팁'
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('ko-KR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const handleCreatePost = (e) => {
    e.preventDefault();
    if (!newTitle.trim() || !newContent.trim()) return;

    createPost(POST_TYPES.COMMUNITY, newTitle, newContent, user, newCategory, []);
    setNewTitle('');
    setNewContent('');
    setNewCategory(CATEGORIES.GENERAL);
    setShowWriteForm(false);
    setMessage('게시글이 등록되었습니다.');
    setTimeout(() => setMessage(''), 3000);
  };

  return (
    <div className="section-container">
      <div className="section-header">
        <h1>💬 {t('community.tabs.community', '자유게시판')}</h1>
        <p>자유롭게 이야기를 나눠보세요</p>
      </div>

      {/* 카테고리 탭 */}
      <div className="category-tabs">
        <button
          className={`category-tab ${selectedCategory === null ? 'active' : ''}`}
          onClick={() => onCategorySelect && onCategorySelect(null)}
        >
          {categoryLabels.all} <span className="count">{categoryCounts.all || 0}</span>
        </button>
        {Object.values(CATEGORIES).map(cat => (
          <button
            key={cat}
            className={`category-tab ${selectedCategory === cat ? 'active' : ''}`}
            onClick={() => onCategorySelect && onCategorySelect(cat)}
          >
            {categoryLabels[cat] || cat} <span className="count">{categoryCounts[cat] || 0}</span>
          </button>
        ))}
      </div>

      {message && <div className="message success">{message}</div>}

      {canPost && !showWriteForm && (
        <button className="write-btn" onClick={() => setShowWriteForm(true)}>
          글쓰기
        </button>
      )}

      {showWriteForm && (
        <form onSubmit={handleCreatePost} className="write-form">
          <select
            value={newCategory}
            onChange={(e) => setNewCategory(e.target.value)}
            className="category-select"
          >
            {Object.values(CATEGORIES).map(cat => (
              <option key={cat} value={cat}>
                {t(`community.categories.${cat}`, cat)}
              </option>
            ))}
          </select>
          <input
            type="text"
            value={newTitle}
            onChange={(e) => setNewTitle(e.target.value)}
            placeholder="제목을 입력하세요"
            required
          />
          <textarea
            value={newContent}
            onChange={(e) => setNewContent(e.target.value)}
            placeholder="내용을 입력하세요"
            rows={6}
            required
          />
          <div className="form-actions">
            <button type="button" onClick={() => setShowWriteForm(false)} className="cancel-btn">
              취소
            </button>
            <button type="submit" className="submit-btn">
              등록
            </button>
          </div>
        </form>
      )}

      <div className="posts-list">
        {posts.length === 0 ? (
          <p className="no-posts">등록된 게시글이 없습니다.</p>
        ) : (
          posts.map((post) => (
            <div key={post.id} className="post-card" onClick={() => onPostSelect(post)}>
              <div className="post-card-header">
                {post.category && (
                  <span className={`post-badge category-${post.category}`}>
                    {t(`community.categories.${post.category}`, post.category)}
                  </span>
                )}
                <h3>{post.title}</h3>
              </div>
              <div className="post-card-meta">
                <span className="author">{post.author.name}</span>
                <span className="date">{formatDate(post.createdAt)}</span>
                <span className="comments">댓글 {post.comments?.length || 0}</span>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

// 이슈게시판 섹션
const IssueBoardSection = ({ onPostSelect }) => {
  const { t } = useTranslation();
  const { user, isAuthenticated } = useAuth();
  const { getCommunityPosts, createPost } = useCommunity();

  const [showWriteForm, setShowWriteForm] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [newContent, setNewContent] = useState('');
  const [message, setMessage] = useState('');

  const canPost = isAuthenticated && user?.tier !== USER_TIERS.GUEST;
  // 이슈 카테고리로 필터링 (임시로 question 카테고리 사용)
  const posts = getCommunityPosts('question');

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('ko-KR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const handleCreatePost = (e) => {
    e.preventDefault();
    if (!newTitle.trim() || !newContent.trim()) return;

    createPost(POST_TYPES.COMMUNITY, newTitle, newContent, user, 'question', []);
    setNewTitle('');
    setNewContent('');
    setShowWriteForm(false);
    setMessage('이슈가 등록되었습니다.');
    setTimeout(() => setMessage(''), 3000);
  };

  return (
    <div className="section-container">
      <div className="section-header">
        <h1>이슈게시판</h1>
        <p>버그 리포트, 기능 요청 등을 공유하세요</p>
      </div>

      {message && <div className="message success">{message}</div>}

      {canPost && !showWriteForm && (
        <button className="write-btn" onClick={() => setShowWriteForm(true)}>
          이슈 등록
        </button>
      )}

      {showWriteForm && (
        <form onSubmit={handleCreatePost} className="write-form">
          <input
            type="text"
            value={newTitle}
            onChange={(e) => setNewTitle(e.target.value)}
            placeholder="이슈 제목"
            required
          />
          <textarea
            value={newContent}
            onChange={(e) => setNewContent(e.target.value)}
            placeholder="이슈 내용을 상세히 작성해주세요"
            rows={6}
            required
          />
          <div className="form-actions">
            <button type="button" onClick={() => setShowWriteForm(false)} className="cancel-btn">
              취소
            </button>
            <button type="submit" className="submit-btn">
              등록
            </button>
          </div>
        </form>
      )}

      <div className="posts-list">
        {posts.length === 0 ? (
          <p className="no-posts">등록된 이슈가 없습니다.</p>
        ) : (
          posts.map((post) => (
            <div key={post.id} className="post-card" onClick={() => onPostSelect(post)}>
              <div className="post-card-header">
                <span className="post-badge category-question">이슈</span>
                <h3>{post.title}</h3>
              </div>
              <div className="post-card-meta">
                <span className="author">{post.author.name}</span>
                <span className="date">{formatDate(post.createdAt)}</span>
                <span className="comments">댓글 {post.comments?.length || 0}</span>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

// 게임 섹션
const GamesSection = () => {
  return (
    <div className="section-container">
      <div className="section-header">
        <h1>게임</h1>
        <p>다양한 미니게임을 즐겨보세요</p>
      </div>
      <div className="coming-soon">
        <span className="icon">🎮</span>
        <h2>준비 중입니다</h2>
        <p>미니게임 기능이 곧 추가됩니다.</p>
      </div>
    </div>
  );
};

// 개발진행현황 섹션 (Family 이상)
const DevProgressSection = () => {
  const { user } = useAuth();
  const hasAccess = user?.tier === USER_TIERS.ADMIN || user?.tier === USER_TIERS.FAMILY;

  const [activeTab, setActiveTab] = useState('board'); // board, archive
  const [tasks, setTasks] = useState([]);
  const [archives, setArchives] = useState([]);
  const [selectedArchiveWeek, setSelectedArchiveWeek] = useState(null);
  const [archiveTasks, setArchiveTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    status: 'backlog',
    priority: 'medium',
    assignee_id: null,
    due_date: '',
    tags: [],
    links: []
  });
  const [tagInput, setTagInput] = useState('');
  const [showDescViewer, setShowDescViewer] = useState(false);
  const [descViewerContent, setDescViewerContent] = useState('');

  // API Base URL
  const API_BASE = 'https://api.ilouli.com/api/community/dev-progress';

  // 토큰 가져오기
  const getToken = () => {
    const cookies = document.cookie.split(';');
    for (let cookie of cookies) {
      const [name, value] = cookie.trim().split('=');
      if (name === 'ilouli_token') {
        return decodeURIComponent(value);
      }
    }
    return localStorage.getItem('ilouli_token_backup') || '';
  };

  // API 호출 함수들
  const fetchTasks = async () => {
    try {
      const response = await fetch(`${API_BASE}/tasks?archived=false`, {
        headers: {
          'Authorization': `Bearer ${getToken()}`
        },
        credentials: 'include'
      });
      const data = await response.json();
      if (data.success) {
        setTasks(data.data);
      }
    } catch (err) {
      setError('작업 목록을 불러올 수 없습니다.');
    } finally {
      setLoading(false);
    }
  };

  const fetchArchives = async () => {
    try {
      const response = await fetch(`${API_BASE}/archives`, {
        headers: {
          'Authorization': `Bearer ${getToken()}`
        },
        credentials: 'include'
      });
      const data = await response.json();
      if (data.success) {
        setArchives(data.data);
      }
    } catch (err) {
      console.error('Failed to fetch archives:', err);
    }
  };

  const fetchArchiveWeek = async (week) => {
    try {
      const response = await fetch(`${API_BASE}/archives/${week}`, {
        headers: {
          'Authorization': `Bearer ${getToken()}`
        },
        credentials: 'include'
      });
      const data = await response.json();
      if (data.success) {
        setArchiveTasks(data.data);
        setSelectedArchiveWeek(week);
      }
    } catch (err) {
      console.error('Failed to fetch archive week:', err);
    }
  };

  // 초기 데이터 로드
  React.useEffect(() => {
    if (hasAccess) {
      fetchTasks();
      fetchArchives();
    }
  }, [hasAccess]);

  // 작업 생성/수정
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title.trim()) return;

    try {
      const url = editingTask
        ? `${API_BASE}/tasks/${editingTask.id}`
        : `${API_BASE}/tasks`;

      const response = await fetch(url, {
        method: editingTask ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${getToken()}`
        },
        credentials: 'include',
        body: JSON.stringify(formData)
      });

      const data = await response.json();
      if (data.success) {
        fetchTasks();
        closeModal();
      } else {
        setError(data.error || '작업 저장에 실패했습니다.');
      }
    } catch (err) {
      setError('작업 저장에 실패했습니다.');
    }
  };

  // 상태 변경
  const handleStatusChange = async (taskId, newStatus) => {
    try {
      const response = await fetch(`${API_BASE}/tasks/${taskId}/status`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${getToken()}`
        },
        credentials: 'include',
        body: JSON.stringify({ status: newStatus })
      });

      const data = await response.json();
      if (data.success) {
        fetchTasks();
      }
    } catch (err) {
      console.error('Failed to update status:', err);
    }
  };

  // 작업 삭제
  const handleDelete = async (taskId) => {
    if (!window.confirm('정말 삭제하시겠습니까?')) return;

    try {
      const response = await fetch(`${API_BASE}/tasks/${taskId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${getToken()}`
        },
        credentials: 'include'
      });

      const data = await response.json();
      if (data.success) {
        fetchTasks();
        closeModal();
      }
    } catch (err) {
      console.error('Failed to delete task:', err);
    }
  };

  // 아카이브 실행
  const handleRunArchive = async () => {
    try {
      const response = await fetch(`${API_BASE}/archives/run`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${getToken()}`
        },
        credentials: 'include'
      });

      const data = await response.json();
      if (data.success) {
        fetchTasks();
        fetchArchives();
        alert(`${data.data.archived_count}개의 작업이 아카이브되었습니다.`);
      }
    } catch (err) {
      console.error('Failed to run archive:', err);
    }
  };

  // 모달 열기/닫기
  const openCreateModal = () => {
    setEditingTask(null);
    setFormData({
      title: '',
      description: '',
      status: 'backlog',
      priority: 'medium',
      assignee_id: null,
      due_date: '',
      tags: [],
      links: []
    });
    setShowModal(true);
  };

  const openEditModal = (task) => {
    setEditingTask(task);
    setFormData({
      title: task.title,
      description: task.description || '',
      status: task.status,
      priority: task.priority,
      assignee_id: task.assignee_id,
      due_date: task.due_date || '',
      tags: task.tags || [],
      links: task.links || []
    });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingTask(null);
  };

  // 태그 추가/삭제
  const addTag = () => {
    if (tagInput.trim() && !formData.tags.includes(tagInput.trim())) {
      setFormData({ ...formData, tags: [...formData.tags, tagInput.trim()] });
      setTagInput('');
    }
  };

  const removeTag = (tag) => {
    setFormData({ ...formData, tags: formData.tags.filter(t => t !== tag) });
  };

  // 설명 뷰어 열기
  const openDescViewer = () => {
    setDescViewerContent(formData.description);
    setShowDescViewer(true);
  };

  // 설명 뷰어 닫기
  const closeDescViewer = () => {
    setShowDescViewer(false);
  };

  // 설명 뷰어에서 저장
  const saveDescViewer = () => {
    setFormData({ ...formData, description: descViewerContent });
    setShowDescViewer(false);
  };

  // 상태별 작업 필터링
  const getTasksByStatus = (status) => {
    return tasks.filter(task => task.status === status);
  };

  // 우선순위 라벨
  const priorityLabels = {
    urgent: '긴급',
    high: '높음',
    medium: '보통',
    low: '낮음'
  };

  // 상태 라벨
  const statusLabels = {
    backlog: '진행예정',
    in_progress: '진행중',
    completed: '완료'
  };

  if (!hasAccess) {
    return (
      <div className="section-container">
        <div className="access-denied">
          <span className="icon">🔒</span>
          <h2>접근 권한이 없습니다</h2>
          <p>이 페이지는 Family 이상 등급만 볼 수 있습니다.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="section-container">
        <div className="coming-soon">
          <p>로딩 중...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="section-container">
      <div className="section-header">
        <h1>🚀 개발진행현황</h1>
        <p>프로젝트 개발 진행 상태를 확인하세요</p>
      </div>

      {error && <div className="message error">{error}</div>}

      <div className="kanban-container">
        <div className="kanban-header">
          <div className="kanban-tabs">
            <button
              className={`kanban-tab ${activeTab === 'board' ? 'active' : ''}`}
              onClick={() => setActiveTab('board')}
            >
              칸반보드
            </button>
            <button
              className={`kanban-tab ${activeTab === 'archive' ? 'active' : ''}`}
              onClick={() => setActiveTab('archive')}
            >
              아카이브
            </button>
          </div>

          <div className="kanban-actions">
            {activeTab === 'board' && (
              <button className="write-btn" onClick={openCreateModal}>
                + 작업 추가
              </button>
            )}
          </div>
        </div>

        {activeTab === 'board' && (
          <div className="kanban-board">
            {['backlog', 'in_progress', 'completed'].map(status => (
              <div key={status} className={`kanban-column ${status}`}>
                <div className="kanban-column-header">
                  <h3 className="kanban-column-title">
                    {statusLabels[status]}
                    <span className="kanban-column-count">
                      {getTasksByStatus(status).length}
                    </span>
                  </h3>
                </div>
                <div className="kanban-tasks">
                  {getTasksByStatus(status).length === 0 ? (
                    <div className="kanban-empty">작업이 없습니다</div>
                  ) : (
                    getTasksByStatus(status).map(task => (
                      <div
                        key={task.id}
                        className="kanban-task"
                        onClick={() => openEditModal(task)}
                      >
                        <h4 className="kanban-task-title">{task.title}</h4>
                        <div className="kanban-task-meta">
                          <span className={`kanban-task-priority ${task.priority}`}>
                            {priorityLabels[task.priority]}
                          </span>
                          {task.tags && task.tags.length > 0 && (
                            <div className="kanban-task-tags">
                              {task.tags.slice(0, 2).map(tag => (
                                <span key={tag} className="kanban-task-tag">{tag}</span>
                              ))}
                              {task.tags.length > 2 && (
                                <span className="kanban-task-tag">+{task.tags.length - 2}</span>
                              )}
                            </div>
                          )}
                        </div>
                        <div className="kanban-task-footer">
                          <div className="kanban-task-date">
                            {task.created_at && new Date(task.created_at).toLocaleDateString('ko-KR')}
                          </div>
                          {task.due_date && (
                            <div className={`kanban-task-due ${new Date(task.due_date) < new Date() && status !== 'completed' ? 'overdue' : ''}`}>
                              📅 {task.due_date}
                            </div>
                          )}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {activeTab === 'archive' && (
          <div className="archive-list">
            {selectedArchiveWeek ? (
              <>
                <button className="back-btn" onClick={() => setSelectedArchiveWeek(null)}>
                  ← 목록으로
                </button>
                <h3>{selectedArchiveWeek} 완료된 작업</h3>
                <div className="archive-tasks-list">
                  {archiveTasks.map(task => (
                    <div key={task.id} className="archive-task-item">
                      <span className="archive-task-check">✓</span>
                      <span className="archive-task-title">{task.title}</span>
                      <span className="archive-task-date">
                        {task.completed_at && new Date(task.completed_at).toLocaleDateString('ko-KR')}
                      </span>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <>
                {archives.length === 0 ? (
                  <div className="kanban-empty">아카이브된 작업이 없습니다</div>
                ) : (
                  archives.map(archive => (
                    <div
                      key={archive.archived_week}
                      className="archive-week-card"
                      onClick={() => fetchArchiveWeek(archive.archived_week)}
                    >
                      <div className="archive-week-header">
                        <h4 className="archive-week-title">{archive.archived_week}</h4>
                        <span className="archive-week-count">{archive.task_count}개 완료</span>
                      </div>
                    </div>
                  ))
                )}
              </>
            )}
          </div>
        )}
      </div>

      {/* 작업 생성/수정 모달 */}
      {showModal && (
        <div className="task-modal-overlay" onClick={closeModal}>
          <div className="task-modal" onClick={e => e.stopPropagation()}>
            <div className="task-modal-header">
              <h2>{editingTask ? '작업 수정' : '새 작업'}</h2>
              <button className="task-modal-close" onClick={closeModal}>×</button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="task-modal-body">
                <div className="task-form-group">
                  <label>제목 *</label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={e => setFormData({ ...formData, title: e.target.value })}
                    placeholder="작업 제목을 입력하세요"
                    required
                  />
                </div>

                <div className="task-form-group">
                  <div className="task-form-label-row">
                    <label>설명</label>
                    <button type="button" className="desc-viewer-btn" onClick={openDescViewer}>
                      자세히 보기
                    </button>
                  </div>
                  <textarea
                    value={formData.description}
                    onChange={e => setFormData({ ...formData, description: e.target.value })}
                    placeholder="작업에 대한 설명을 입력하세요"
                    rows={8}
                  />
                </div>

                <div className="task-form-row">
                  <div className="task-form-group">
                    <label>상태</label>
                    <select
                      value={formData.status}
                      onChange={e => setFormData({ ...formData, status: e.target.value })}
                    >
                      <option value="backlog">진행예정</option>
                      <option value="in_progress">진행중</option>
                      <option value="completed">완료</option>
                    </select>
                  </div>

                  <div className="task-form-group">
                    <label>우선순위</label>
                    <select
                      value={formData.priority}
                      onChange={e => setFormData({ ...formData, priority: e.target.value })}
                    >
                      <option value="urgent">긴급</option>
                      <option value="high">높음</option>
                      <option value="medium">보통</option>
                      <option value="low">낮음</option>
                    </select>
                  </div>
                </div>

                <div className="task-form-group">
                  <label>예상 완료일</label>
                  <input
                    type="date"
                    value={formData.due_date}
                    onChange={e => setFormData({ ...formData, due_date: e.target.value })}
                  />
                </div>

                <div className="task-form-group">
                  <label>태그</label>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <input
                      type="text"
                      value={tagInput}
                      onChange={e => setTagInput(e.target.value)}
                      onKeyPress={e => e.key === 'Enter' && (e.preventDefault(), addTag())}
                      placeholder="태그 입력 후 Enter"
                      style={{ flex: 1 }}
                    />
                    <button type="button" className="cancel-btn" onClick={addTag}>추가</button>
                  </div>
                  {formData.tags.length > 0 && (
                    <div className="task-form-tags">
                      {formData.tags.map(tag => (
                        <span key={tag} className="task-form-tag">
                          {tag}
                          <button type="button" onClick={() => removeTag(tag)}>×</button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div className="task-modal-footer">
                <div className="task-modal-footer-left">
                  {editingTask && (
                    <button type="button" className="delete-btn" onClick={() => handleDelete(editingTask.id)}>
                      삭제
                    </button>
                  )}
                </div>
                <div className="task-modal-footer-right">
                  <button type="button" className="cancel-btn" onClick={closeModal}>취소</button>
                  <button type="submit" className="submit-btn">{editingTask ? '수정' : '생성'}</button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* 설명 자세히 보기/편집 모달 */}
      {showDescViewer && (
        <div className="desc-viewer-overlay" onClick={closeDescViewer}>
          <div className="desc-viewer-modal" onClick={e => e.stopPropagation()}>
            <div className="desc-viewer-header">
              <h2>설명 편집</h2>
              <button className="desc-viewer-close" onClick={closeDescViewer}>×</button>
            </div>

            <div className="desc-viewer-body">
              <textarea
                className="desc-viewer-textarea"
                value={descViewerContent}
                onChange={e => setDescViewerContent(e.target.value)}
                placeholder="작업에 대한 설명을 입력하세요"
                autoFocus
              />
            </div>

            <div className="desc-viewer-footer">
              <button type="button" className="cancel-btn" onClick={closeDescViewer}>취소</button>
              <button type="button" className="submit-btn" onClick={saveDescViewer}>저장</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// PRD 문서 섹션 (Family 이상)
const PRDSection = () => {
  const { user } = useAuth();
  const hasAccess = user?.tier === USER_TIERS.ADMIN || user?.tier === USER_TIERS.FAMILY;

  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [editingDoc, setEditingDoc] = useState(null);
  const [viewingDoc, setViewingDoc] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    content: '',
    status: '작성중',
    project: '',
    tags: []
  });

  const API_BASE = 'https://api.ilouli.com/api/community/prd';

  const getToken = () => {
    const cookies = document.cookie.split(';');
    for (let cookie of cookies) {
      const [name, value] = cookie.trim().split('=');
      if (name === 'ilouli_token') {
        return decodeURIComponent(value);
      }
    }
    return localStorage.getItem('ilouli_token_backup') || '';
  };

  const fetchDocs = async () => {
    try {
      const response = await fetch(API_BASE, {
        headers: { 'Authorization': `Bearer ${getToken()}` },
        credentials: 'include'
      });
      const data = await response.json();
      if (data.success) {
        setDocs(data.data);
      }
    } catch (err) {
      setError('PRD 문서를 불러올 수 없습니다.');
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    if (hasAccess) {
      fetchDocs();
    }
  }, [hasAccess]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title.trim()) return;

    try {
      const url = editingDoc ? `${API_BASE}/${editingDoc.id}` : API_BASE;
      const response = await fetch(url, {
        method: editingDoc ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${getToken()}`
        },
        credentials: 'include',
        body: JSON.stringify(formData)
      });

      const data = await response.json();
      if (data.success) {
        fetchDocs();
        closeModal();
      } else {
        setError(data.error || '저장에 실패했습니다.');
      }
    } catch (err) {
      setError('저장에 실패했습니다.');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('정말 삭제하시겠습니까?')) return;

    try {
      const response = await fetch(`${API_BASE}/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${getToken()}` },
        credentials: 'include'
      });

      const data = await response.json();
      if (data.success) {
        fetchDocs();
        closeModal();
        closeViewModal();
      }
    } catch (err) {
      console.error('Failed to delete:', err);
    }
  };

  const openCreateModal = () => {
    setEditingDoc(null);
    setFormData({
      title: '',
      description: '',
      content: '',
      status: '작성중',
      project: '',
      tags: []
    });
    setShowModal(true);
  };

  const openEditModal = (doc) => {
    setEditingDoc(doc);
    setFormData({
      title: doc.title,
      description: doc.description || '',
      content: doc.content || '',
      status: doc.status,
      project: doc.project || '',
      tags: doc.tags || []
    });
    setShowModal(true);
  };

  const openViewModal = (doc) => {
    setViewingDoc(doc);
    setShowViewModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingDoc(null);
  };

  const closeViewModal = () => {
    setShowViewModal(false);
    setViewingDoc(null);
  };

  const statusOptions = ['작성중', '검토중', '승인됨', '개발중', '완료', '보류'];

  if (!hasAccess) {
    return (
      <div className="section-container">
        <div className="access-denied">
          <span className="icon">🔒</span>
          <h2>접근 권한이 없습니다</h2>
          <p>이 페이지는 Family 이상 등급만 볼 수 있습니다.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="section-container">
        <div className="coming-soon"><p>로딩 중...</p></div>
      </div>
    );
  }

  return (
    <div className="section-container">
      <div className="section-header">
        <h1>📄 PRD 문서</h1>
        <p>제품 요구사항 정의서 목록</p>
      </div>

      {error && <div className="message error">{error}</div>}

      <button className="write-btn" onClick={openCreateModal}>
        + 새 PRD 작성
      </button>

      <div className="posts-list">
        {docs.length === 0 ? (
          <p className="no-posts">등록된 PRD 문서가 없습니다.</p>
        ) : (
          docs.map((doc) => (
            <div key={doc.id} className="post-card" onClick={() => openViewModal(doc)}>
              <div className="post-card-header">
                <span className={`post-badge ${doc.status === '개발중' ? 'category-question' : doc.status === '완료' ? 'category-general' : 'category-tip'}`}>
                  {doc.status}
                </span>
                <h3>{doc.title}</h3>
              </div>
              {doc.description && <p className="post-description">{doc.description}</p>}
              <div className="post-card-meta">
                <span className="date">{new Date(doc.created_at).toLocaleDateString('ko-KR')}</span>
                {doc.project && <span className="project">{doc.project}</span>}
              </div>
            </div>
          ))
        )}
      </div>

      {/* PRD 생성/수정 모달 */}
      {showModal && (
        <div className="task-modal-overlay" onClick={closeModal}>
          <div className="task-modal prd-modal" onClick={e => e.stopPropagation()}>
            <div className="task-modal-header">
              <h2>{editingDoc ? 'PRD 수정' : '새 PRD'}</h2>
              <button className="task-modal-close" onClick={closeModal}>×</button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="task-modal-body">
                <div className="task-form-group">
                  <label>제목 *</label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={e => setFormData({ ...formData, title: e.target.value })}
                    placeholder="PRD 제목"
                    required
                  />
                </div>

                <div className="task-form-group">
                  <label>설명</label>
                  <input
                    type="text"
                    value={formData.description}
                    onChange={e => setFormData({ ...formData, description: e.target.value })}
                    placeholder="간단한 설명"
                  />
                </div>

                <div className="task-form-row">
                  <div className="task-form-group">
                    <label>상태</label>
                    <select
                      value={formData.status}
                      onChange={e => setFormData({ ...formData, status: e.target.value })}
                    >
                      {statusOptions.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                  <div className="task-form-group">
                    <label>프로젝트</label>
                    <input
                      type="text"
                      value={formData.project}
                      onChange={e => setFormData({ ...formData, project: e.target.value })}
                      placeholder="프로젝트명"
                    />
                  </div>
                </div>

                <div className="task-form-group">
                  <label>내용</label>
                  <textarea
                    value={formData.content}
                    onChange={e => setFormData({ ...formData, content: e.target.value })}
                    placeholder="PRD 내용 (마크다운 지원)"
                    rows={12}
                  />
                </div>
              </div>

              <div className="task-modal-footer">
                <div className="task-modal-footer-left">
                  {editingDoc && (
                    <button type="button" className="delete-btn" onClick={() => handleDelete(editingDoc.id)}>삭제</button>
                  )}
                </div>
                <div className="task-modal-footer-right">
                  <button type="button" className="cancel-btn" onClick={closeModal}>취소</button>
                  <button type="submit" className="submit-btn">{editingDoc ? '수정' : '생성'}</button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* PRD 보기 모달 */}
      {showViewModal && viewingDoc && (
        <div className="desc-viewer-overlay" onClick={closeViewModal}>
          <div className="desc-viewer-modal" onClick={e => e.stopPropagation()}>
            <div className="desc-viewer-header">
              <h2>{viewingDoc.title}</h2>
              <button className="desc-viewer-close" onClick={closeViewModal}>×</button>
            </div>

            <div className="desc-viewer-body">
              <div className="prd-view-meta">
                <span className={`post-badge ${viewingDoc.status === '개발중' ? 'category-question' : 'category-general'}`}>
                  {viewingDoc.status}
                </span>
                <span className="date">{new Date(viewingDoc.created_at).toLocaleDateString('ko-KR')}</span>
                {viewingDoc.project && <span className="project">{viewingDoc.project}</span>}
              </div>
              {viewingDoc.description && <p className="prd-view-desc">{viewingDoc.description}</p>}
              <div className="prd-view-content">
                {viewingDoc.content ? (
                  viewingDoc.content.split('\n').map((line, i) => (
                    <p key={i}>{line || '\u00A0'}</p>
                  ))
                ) : (
                  <p className="desc-viewer-empty">내용이 없습니다.</p>
                )}
              </div>
            </div>

            <div className="desc-viewer-footer">
              <button type="button" className="cancel-btn" onClick={closeViewModal}>닫기</button>
              <button type="button" className="submit-btn" onClick={() => { closeViewModal(); openEditModal(viewingDoc); }}>편집</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// 글 상세 보기
const PostDetail = ({ post, onBack }) => {
  const { t } = useTranslation();
  const { user, isAuthenticated } = useAuth();
  const { deletePost, addComment, deleteComment, getPostById } = useCommunity();
  const { addNotification } = useNotification();

  const [currentPost, setCurrentPost] = useState(post);
  const [newComment, setNewComment] = useState('');
  const [message, setMessage] = useState('');

  const isAdmin = user?.tier === USER_TIERS.ADMIN;
  const canPost = isAuthenticated && user?.tier !== USER_TIERS.GUEST;

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('ko-KR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleDeletePost = () => {
    if (window.confirm('정말 삭제하시겠습니까?')) {
      try {
        deletePost(currentPost.id, user.id, isAdmin);
        onBack();
      } catch (err) {
        setMessage('삭제에 실패했습니다.');
      }
    }
  };

  const handleAddComment = (e) => {
    e.preventDefault();
    if (!newComment.trim() || !currentPost) return;

    addComment(currentPost.id, newComment, user);
    setNewComment('');

    // 글 새로고침
    const updated = getPostById(currentPost.id);
    setCurrentPost(updated);

    // 알림 발송
    if (currentPost.author.id !== user.id) {
      addNotification(
        currentPost.author.id,
        NOTIFICATION_TYPES.COMMENT,
        `${user.displayName || user.nickname}님이 댓글을 남겼습니다`,
        `"${currentPost.title}"`,
        `/test`
      );
    }
  };

  const handleDeleteComment = (commentId) => {
    if (window.confirm('댓글을 삭제하시겠습니까?')) {
      try {
        deleteComment(currentPost.id, commentId, user.id, isAdmin);
        const updated = getPostById(currentPost.id);
        setCurrentPost(updated);
      } catch (err) {
        setMessage('댓글 삭제에 실패했습니다.');
      }
    }
  };

  return (
    <div className="section-container">
      <button className="back-btn" onClick={onBack}>
        ← 목록으로
      </button>

      {message && <div className="message error">{message}</div>}

      <article className="post-detail">
        <header className="post-detail-header">
          <div className="post-badges">
            {currentPost.type === POST_TYPES.ANNOUNCEMENT && (
              <span className="post-badge announcement">공지</span>
            )}
            {currentPost.category && (
              <span className={`post-badge category-${currentPost.category}`}>
                {t(`community.categories.${currentPost.category}`, currentPost.category)}
              </span>
            )}
          </div>
          <h1>{currentPost.title}</h1>
          <div className="post-meta">
            <span className="author">{currentPost.author.name}</span>
            <span className="date">{formatDate(currentPost.createdAt)}</span>
          </div>
        </header>

        <div className="post-content">
          {currentPost.content.split('\n').map((line, i) => (
            <p key={i}>{line}</p>
          ))}
        </div>

        {(currentPost.author.id === user?.id || isAdmin) && (
          <div className="post-actions">
            <button onClick={handleDeletePost} className="delete-btn">
              삭제
            </button>
          </div>
        )}
      </article>

      {/* 댓글 섹션 */}
      <section className="comments-section">
        <h2>댓글 ({currentPost.comments?.length || 0})</h2>

        {canPost && (
          <form onSubmit={handleAddComment} className="comment-form">
            <textarea
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              placeholder="댓글을 입력하세요"
              rows={3}
            />
            <button type="submit" disabled={!newComment.trim()}>
              댓글 작성
            </button>
          </form>
        )}

        <div className="comments-list">
          {(!currentPost.comments || currentPost.comments.length === 0) ? (
            <p className="no-comments">아직 댓글이 없습니다.</p>
          ) : (
            currentPost.comments.map((comment) => (
              <div key={comment.id} className="comment-item">
                <div className="comment-header">
                  <span className="comment-author">{comment.author.name}</span>
                  <span className="comment-date">{formatDate(comment.createdAt)}</span>
                </div>
                <p className="comment-content">{comment.content}</p>
                {(comment.author.id === user?.id || isAdmin) && (
                  <button
                    onClick={() => handleDeleteComment(comment.id)}
                    className="comment-delete-btn"
                  >
                    삭제
                  </button>
                )}
              </div>
            ))
          )}
        </div>
      </section>
    </div>
  );
};

// 메인 TestPage 컴포넌트
const TestPage = () => {
  const location = useLocation();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedPost, setSelectedPost] = useState(null);

  // 라우트 변경 시 게시글 상세보기 초기화
  React.useEffect(() => {
    setSelectedPost(null);
  }, [location.pathname]);

  // 현재 활성 섹션 결정
  const getActiveSection = () => {
    const path = location.pathname;
    if (path.includes('/announcements')) return 'announcements';
    if (path.includes('/free-board')) return 'free-board';
    if (path.includes('/youtube-trends')) return 'youtube-trends';
    if (path.includes('/dev-progress')) return 'dev-progress';
    return 'announcements'; // default
  };

  const activeSection = getActiveSection();

  // 글 상세보기에서 목록으로 돌아가기
  const handleBackToList = () => {
    setSelectedPost(null);
  };

  // 글 선택
  const handlePostSelect = (post) => {
    setSelectedPost(post);
  };

  // 글 상세 보기 중일 때
  if (selectedPost) {
    return (
      <div className="test-page-layout">
        <Sidebar
          collapsed={sidebarCollapsed}
          onToggle={() => setSidebarCollapsed(!sidebarCollapsed)}
          selectedCategory={selectedCategory}
          onCategorySelect={setSelectedCategory}
          activeSection={activeSection}
          basePath="/test"
        />
        <main className="test-page-content">
          <PostDetail post={selectedPost} onBack={handleBackToList} />
        </main>
      </div>
    );
  }

  return (
    <div className="test-page-layout">
      <Sidebar
        collapsed={sidebarCollapsed}
        onToggle={() => setSidebarCollapsed(!sidebarCollapsed)}
        selectedCategory={selectedCategory}
        onCategorySelect={setSelectedCategory}
        activeSection={activeSection}
        basePath="/test"
      />
      <main className="test-page-content">
        <Routes>
          <Route path="/" element={<Navigate to="/test/announcements" replace />} />
          <Route path="/announcements" element={<AnnouncementsSection onPostSelect={handlePostSelect} />} />
          <Route path="/free-board" element={<FreeBoardSection onPostSelect={handlePostSelect} selectedCategory={selectedCategory} onCategorySelect={setSelectedCategory} />} />
          <Route path="/issue-board" element={<IssueBoardSection onPostSelect={handlePostSelect} />} />
          <Route path="/games" element={<GamesSection />} />
          <Route path="/dev-progress" element={<DevProgressSection />} />
          <Route path="/prd" element={<PRDSection />} />
        </Routes>
      </main>
    </div>
  );
};

export default TestPage;
